import { assetServices, companyServices, contractServices } from '@/services'
import { AttachmentUploader } from '@/shared/components/attachment-uploader'
import { Pill } from '@/shared/components/pill'
import { store } from '@/store'
import { assetV2Api } from '@/store/services'
import { useCreateAttachmentReplacementCandidateMutation } from '@/store/services/attachment-replacement-candidate.service'
import { useUpdateAttachmentMutation } from '@/store/services/attachment.service'
import { StatusChip } from '@/components/status-chip'
import { attachmentChipStatuses } from '@/constants/attachment-chip-statuses'
import { getVisibleRejectedReplacementCandidate } from '@/utils/attachment-replacement'
import moment from '@/config/moment'
import {
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  Spinner,
  Typography
} from '@material-tailwind/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath, useLoaderData, useNavigate, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

export const PendingAttachmentUpload = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentResource, setCurrentResource] = useState({})
  const [uploadPhase, setUploadPhase] = useState('idle')
  const { attachment: currentAttachment = {} } = useLoaderData()
  const { id: routeId, companyId, attachmentId } = useParams()
  const currentUrlParams = useLocation().search

  const [updateAttachment, { isLoading }] =
    useUpdateAttachmentMutation()
  const [createAttachmentReplacementCandidate, { isLoading: isCreatingReplacement }] =
    useCreateAttachmentReplacementCandidateMutation()

  const returnPath = useMemo(() => {
    if (attachmentId && routeId) {
      if (currentAttachment?.attachableType === 'Contractor::Company') {
        return generatePath('/companies/:id/attachments', { id: routeId })
      }

      if (currentAttachment?.attachableType === 'Contractor::Contract') {
        return generatePath('/contracts/:id/attachments/list', { id: routeId })
      }

      if (currentAttachment?.attachableType === 'Asset') {
        return generatePath('/assets_v2/:id/attachments', { id: routeId })
      }
    }

    if (companyId) {
      return generatePath('/companies/:id/attachments', { id: companyId })
    }

    return '/attachments'
  }, [attachmentId, companyId, currentAttachment?.attachableType, routeId])

  const navigateBackWithReload = () => {
    const nextSearchParams = new URLSearchParams(currentUrlParams)
    nextSearchParams.set('attachment_reload', String(Date.now()))
    const nextQuery = nextSearchParams.toString()

    navigate(nextQuery ? `${returnPath}?${nextQuery}` : returnPath)
  }

  const onUpload = async (values) => {
    if (!values.document.filename) {
      toast.warn('Adjunta un documento antes de actualizar.')
      return
    }

    if (currentAttachment?.openReplacementCandidate?.id) {
      toast.warn('Este documento ya tiene un reemplazo en revisión o programado.')
      return
    }

    try {
      setUploadPhase('uploading')
      if (currentAttachment.status === 'approved') {
        const effectiveMode = values.replacement_effective_mode || 'immediate'

        await createAttachmentReplacementCandidate({
          replaceable_type: 'Attachment',
          replaceable_id: currentAttachment.id,
          effective_mode: effectiveMode,
          effective_on: effectiveMode === 'scheduled' ? values.replacement_effective_on : null,
          expiration_date: values.expiration_date,
          document: values.document
        }).unwrap()
      } else {
        await updateAttachment(values).unwrap()
      }
      setUploadPhase('returning')
      navigateBackWithReload()
    } catch (error) {
      setUploadPhase('idle')
      throw error
    }
  }

  const attachmentTypeOptions = useMemo(() => {
    return currentAttachment?.attachmentType ? [currentAttachment.attachmentType] : []
  }, [currentAttachment])

  const expirationNoticeEntity = useMemo(() => {
    if (currentAttachment.attachableType === 'Contractor::Asset') return 'el activo'
    if (currentAttachment.attachableType === 'Asset') return 'el activo'
    if (currentAttachment.attachableType === 'Contractor::Contract') return 'el contrato'
    if (currentAttachment.attachableType === 'Contractor::Company') return 'el contratista'

    return 'la entidad'
  }, [currentAttachment.attachableType])

  const rejectedReplacementCandidate = useMemo(
    () => getVisibleRejectedReplacementCandidate(currentAttachment),
    [currentAttachment]
  )

  const replacementEffectiveOnMax = useMemo(() => {
    if (!currentAttachment?.expirationDate) return undefined

    return moment(currentAttachment.expirationDate).add(1, 'day').format('YYYY-MM-DD')
  }, [currentAttachment?.expirationDate])

  const initialValues = useMemo(() => {
    if (!currentAttachment?.id || !currentAttachment?.attachmentType?.id) return {}

    const keepExpiration =
      ['pending', 'pending_upload', 'rejected', 'expired'].includes(currentAttachment.status)

    return {
      id: currentAttachment.id,
      attachment_type_id: currentAttachment.attachmentType.id,
      document: {},
      expiration_date: keepExpiration ? currentAttachment.expirationDate : null,
      period_month: currentAttachment.periodMonth,
      period_year: currentAttachment.periodYear,
      status: currentAttachment.status === 'approved' ? 'approved' : 'pending',
      replacement_effective_mode: 'immediate',
      replacement_effective_on: null
    }
  }, [currentAttachment])

  const resourcePromise = () => {
    if (currentAttachment.attachableType === 'Contractor::Contract') {
      return contractServices.show({ id: currentAttachment.attachableId })
    }

    if (currentAttachment.attachableType === 'Contractor::Asset') {
      return assetServices.show({ id: currentAttachment.attachableId })
    }

    if (currentAttachment.attachableType === 'Contractor::Company') {
      return companyServices.show({ id: currentAttachment.attachableId })
    }

    if (currentAttachment.attachableType === 'Asset') {
      return store.dispatch(
        assetV2Api.endpoints.getAssetV2.initiate(currentAttachment.attachableId, {
          forceRefetch: true
        })
      ).unwrap()
    }
  }

  useEffect(() => {
    if (!currentAttachment.id) return

    const fetchResource = async () => {
      const response = await resourcePromise()
      const resource = response?.data
        ? Object.values(response.data)[0] || {}
        : response || {}
      setCurrentResource(resource)
    }

    fetchResource()
  }, [currentAttachment])

  const pillOptions = useMemo(() => {
    if (!currentResource.id) return []

    if (currentResource.klassName === 'Contractor::Contract') {
      return [
        { label: t('company.name'), value: currentResource.company.name },
        {
          label: t('documentNumber.one'),
          value: currentResource.company.documentNumber
        },
        { label: t('contract.id'), value: currentResource.id }
      ]
    }

    if (currentAttachment.attachableType === 'Contractor::Asset') {
      return [
        {
          label: t('asset.vehicle.type'),
          value: currentResource.vehicleInfo.type
        },
        {
          label: t('asset.vehicle.patent'),
          value: currentResource.vehicleInfo.patent
        },
        { label: t('contract.id'), value: currentResource.contract.id },
        {
          label: t('documentNumber.one'),
          value: currentResource.contract.company.documentNumber
        }
      ]
    }

    if (currentAttachment.attachableType === 'Contractor::Company') {
      return [
        { label: t('company.name'), value: currentResource.name },
        {
          label: t('documentNumber.one'),
          value: currentResource.documentNumber
        }
      ]
    }

    if (currentAttachment.attachableType === 'Asset') {
      return [
        {
          label: currentResource.asset_type?.identifier_label || 'Identificador',
          value: currentResource.identifier_value
        },
        {
          label: t('contract.one'),
          value: `${currentResource.contractor_contract?.id || '-'} - ${currentResource.contractor_contract?.name || '-'}`
        },
        {
          label: t('contractor.one'),
          value: currentResource.contractor_contract?.company?.name
        }
      ]
    }

    return []
  }, [currentAttachment.attachableType, currentResource, t])

  const isReady = attachmentTypeOptions.length > 0 && Object.keys(initialValues).length > 0
  const isBusy = isLoading || isCreatingReplacement || uploadPhase !== 'idle'
  const loadingMessage = uploadPhase === 'returning'
    ? 'Actualizando listado...'
    : 'Subiendo documento...'

  return (
    <Dialog open size='lg'>
      <DialogHeader>Carga de documentos</DialogHeader>

      <DialogBody className='flex gap-4'>
        {pillOptions.map((pillOption) => (
          <Pill key={pillOption.label} {...pillOption} />
        ))}
      </DialogBody>

      <DialogBody className='relative'>
        {isBusy && (
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]'>
            <div className='flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-md'>
              <Spinner className='h-5 w-5' />
              <Typography>{loadingMessage}</Typography>
            </div>
          </div>
        )}
        {rejectedReplacementCandidate?.id && (
          <div className='mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-900'>
            <div className='mb-2'>
              <StatusChip
                options={attachmentChipStatuses}
                value='replacement_rejected'
              />
            </div>
            <Typography className='text-sm font-medium'>
              El último archivo de reemplazo fue rechazado.
            </Typography>
            <Typography className='text-sm'>
              {rejectedReplacementCandidate.reviewComment || 'Sin comentario de revisión.'}
            </Typography>
          </div>
        )}
        {isReady ? (
          <AttachmentUploader
            isDisabled
            attachableType={currentAttachment.attachableType}
            attachmentTypes={attachmentTypeOptions}
            initialValues={initialValues}
            showDefaultHeader={false}
            onUpload={onUpload}
            restrictPastExpirationDates
            expirationDateNotice={t('common.uploadDocuments.pendingExpirationNotice', {
              entity: expirationNoticeEntity
            })}
            replacementModeEnabled={currentAttachment.status === 'approved'}
            replacementEffectiveOnMax={replacementEffectiveOnMax}
          >
            {(handleSubmit) => (
              <div className='flex justify-between'>
                <Link to={`${returnPath}${currentUrlParams}`}>
                  <Button
                    variant='text'
                    disabled={isBusy}
                  >
                    Cancelar
                  </Button>
                </Link>
                <Button loading={isBusy} disabled={isBusy || !!currentAttachment?.openReplacementCandidate?.id} onClick={handleSubmit}>
                  Cargar archivo
                </Button>
              </div>
            )}
          </AttachmentUploader>
        ) : (
          <div className='flex min-h-48 items-center justify-center gap-3 text-gray-600'>
            <Spinner className='h-5 w-5' />
            <Typography>Preparando formulario...</Typography>
          </div>
        )}
      </DialogBody>
    </Dialog >
  )
}
